import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FILTER_VALUE_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import { VerifyTermApi } from "../../../../../../../../services/Api/Organization/organizationTypes";
import {
	AvailableSubscriptionContentsApi,
	SubscriptionApi,
	SubscriptionInvoiceApi,
	SubscriptionVisibility,
	SubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { OrganizationMeApi } from "../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { selectUser } from "../../../../../../../../services/users/selectors";
import SubscriptionsMobilePreview from "../../components/Preview/Mobile/SubscriptionsMobilePreview";
import SubscriptionTermsAlert from "../../components/Preview/SubscriptionTermsAlert";
import SubscriptionsPreview from "../../components/Preview/SubscriptionsPreview";
import SubscriptionErrorsDisplay from "../../components/SubscriptionErrorsDisplay";
import SubscriptionsExpirationDate from "../../components/SubscriptionsExpirationDate";

const TranslationsInitialized = () => {
	const { t } = useTranslation();

	t("enums.billing_period.MONTHLY");
	t("enums.billing_period.YEARLY");
};

const OrganizationSubscriptionsIndexPage = () => {
	const [subscriptions, setSubscriptions] = useState<SubscriptionsApi>();
	const [meDetails, setMeDetails] = useState<OrganizationMeApi | undefined>(undefined);
	const [invoices, setInvoices] = useState<SubscriptionInvoiceApi[] | undefined>(undefined);
	const [unpaidProformas, setUnpaidProformas] = useState<SubscriptionInvoiceApi[] | undefined>(undefined);
	const [subscriptionsVisibility, setSubscriptionsVisibility] = useState<SubscriptionVisibility | undefined>();
	const organization = useSelector(selectOrganization);
	const user = useSelector(selectUser);
	const [errors, setErrors] = useState<undefined | ApiError[]>(undefined);
	const [syncLoading, setSyncLoading] = useState<boolean>(false);
	const [migrateSubscriptionsLoading, setMigrateSubscriptionsLoading] = useState<boolean>(false);
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const confirmation = useConfirmation();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const [termsToAccept, setTermsToAccept] = useState<VerifyTermApi[]>([]);

	useEffect(() => {
		handleChangeTabTitle(t("modules.subscription.header.title"));
		fetch();
	}, []);

	const fetch = () => {
		organizationApi
			.getSubscriptions()
			.then(async (res) => {
				setSubscriptions(res.data.data);
				const configRes = await fetchAvailableContents();
				await fetchOrganizationMeDetails();
				await fetchInvoices();
				await fetchSubscriptionsVisibility();
				await fetchUnpaidInvoices();
				await fetchTermsToVerify(getTermNames(configRes, res.data.data.subscriptions));
			})
			.catch((err) => {
				handleError.alert(err, addFlash, ["customer_not_found"]);
				const errors = handleException(err);
				setErrors(errors);
			});
	};

	const fetchUnpaidInvoices = async () => {
		try {
			const params: Record<string, any> = {
				f: btoa(
					unescape(
						encodeURIComponent(
							`${NEW_WAY_TO_ENCODING_FILTER_SIGN}status=NOT_PAID${FILTER_VALUE_SEPARATOR}PAYMENT_DUE`
						)
					)
				),
			};
			const res = await organizationApi.getSubscriptionInvoices(params);
			setUnpaidProformas(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchOrganizationMeDetails = async () => {
		try {
			if (organization && organization.id) {
				const res = await organizationApi.getOrganizationMe(organization.id);
				setMeDetails(res);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchInvoices = async () => {
		try {
			const date = new Date();
			const params: Record<string, any> = {
				f: btoa(
					unescape(
						encodeURIComponent(
							`${NEW_WAY_TO_ENCODING_FILTER_SIGN}due_date|gt=${date.getFullYear()}-${
								date.getMonth() < 10 ? `0${date.getMonth()}` : date.getMonth()
							}-01`
						)
					)
				),
			};
			const res = await organizationApi.getSubscriptionInvoices(params);
			setInvoices(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const sync = async () => {
		setSyncLoading(true);
		try {
			await organizationApi.syncSubscription();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSyncLoading(false);
	};

	const fetchSubscriptionsVisibility = async () => {
		try {
			const res = await organizationApi.getSubscriptionsVisibility();
			setSubscriptionsVisibility(res.visibility);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onMigrateSubscription = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			setMigrateSubscriptionsLoading(true);
			await organizationApi.migrateSubscription();
			fetch();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setMigrateSubscriptionsLoading(false);
	};

	const fetchTermsToVerify = async (termNames: string[]) => {
		try {
			const res = await organizationApi.verifyTerms(termNames);
			const terms = res.required_terms;
			setTermsToAccept(terms.filter((term) => !term.signed));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchAvailableContents = async () => {
		try {
			const res = await organizationApi.getAvailableSubscriptionContents();
			return res.data.data.available_contents;
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};
	const getTermNames = (config: AvailableSubscriptionContentsApi, subscriptions?: SubscriptionApi[]) => {
		const termNames: string[] = [];

		subscriptions?.forEach((subscription) => {
			const subscriptionConfig = config.contents.find((content) => content.family_id === subscription.family_id);
			const subscriptionPlan = subscriptionConfig?.plans?.find((plan) => plan.plan_id === subscription.plan_id);
			if (subscriptionPlan) {
				subscription.addons.forEach((addon) => {
					const addonConfig = subscriptionPlan.addons.find(({ addon_id }) => addon_id === addon.addon_id);
					if (addonConfig && addonConfig.terms && addonConfig.terms.name) {
						termNames.push(addonConfig.terms.name);
					}
				});
			}
		});

		return termNames;
	};

	if (!subscriptionsVisibility) return <LoadingContainer />;

	const buttons: ButtonProps[] = [
		{
			title: t("modules.subscription.action.synchronize.title"),
			action: sync,
			loading: syncLoading,
			variant: "primary",
		},
		{
			title: t("modules.subscription.action.migrate.title"),
			dropdown: true,
			action: onMigrateSubscription,
			hide: !(user && user.admin),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.subscription.action.synchronize.title"),
			action: sync,
		},
		{
			title: t("modules.subscription.action.migrate.title"),
			action: onMigrateSubscription,
			hide: !(user && user.admin),
		},
	];

	return (
		<>
			<div className={"content-preview"}>
				<Header
					title={t("modules.subscription.header.title")}
					buttons={isMobile ? [] : buttons}
					customHeaderContent={
						<SubscriptionsExpirationDate
							subscriptions={subscriptions?.subscriptions || []}
							className="me-2"
						/>
					}
				/>
				{isMobile && subscriptions?.subscriptions && subscriptions?.subscriptions?.length > 0 && (
					<hr style={{ color: "#E9ECEF", opacity: 1 }} />
				)}
				<MobileActions actions={mobileActions} />
				{errors ? (
					<>
						<SubscriptionErrorsDisplay errors={errors} />
					</>
				) : (
					<>
						{!subscriptions ||
						!meDetails ||
						!invoices ||
						migrateSubscriptionsLoading ||
						!unpaidProformas ? (
							<LoadingContainer />
						) : (
							<>
								{(subscriptionsVisibility === SubscriptionVisibility.HIDDEN ||
									subscriptionsVisibility === SubscriptionVisibility.READ_ONLY) && (
									<Alert variant={"danger"}>
										<span className={"me-1"}>
											{t("modules.subscription.field.subscriptions_read_only.title")}
										</span>
									</Alert>
								)}
								{termsToAccept.length !== 0 && (
									<SubscriptionTermsAlert terms={termsToAccept} setTerms={setTermsToAccept} />
								)}
								{isMobile ? (
									<SubscriptionsMobilePreview
										subscriptions={subscriptions}
										meDetails={meDetails}
										invoices={invoices}
										unpaidProformas={unpaidProformas}
										subscriptionsVisibility={subscriptionsVisibility}
										onFetchOrganizationMeDetails={fetchOrganizationMeDetails}
										termsToAccept={termsToAccept}
									/>
								) : (
									<SubscriptionsPreview
										subscriptions={subscriptions}
										meDetails={meDetails}
										invoices={invoices}
										unpaidProformas={unpaidProformas}
										subscriptionsVisibility={subscriptionsVisibility}
										onFetchOrganizationMeDetails={fetchOrganizationMeDetails}
										termsToAccept={termsToAccept}
									/>
								)}
							</>
						)}
					</>
				)}
			</div>
		</>
	);
};
export default OrganizationSubscriptionsIndexPage;
